import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import api from '../../lib/api';
import { formatCurrency } from '../../mock';

export const ProfitReport = () => {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: first, to: today.toISOString().slice(0, 10) });
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/profit-report?date_from=${range.from}&date_to=${range.to}`);
      setP(data);
    } catch (e) { setP(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const dlCsv = async () => {
    try {
      const res = await api.get(`/profit-report/csv?date_from=${range.from}&date_to=${range.to}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'relatorio_profit.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {}
  };

  const inputCls = 'bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary';
  return (
    <div data-testid="profit-report">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <h3 className="font-display font-black text-lg uppercase tracking-wide mr-auto">Profit (Front + Back-End)</h3>
        <div><label className="text-[10px] text-muted-foreground block mb-1">De</label><input data-testid="profit-from" type="date" className={inputCls} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></div>
        <div><label className="text-[10px] text-muted-foreground block mb-1">Até</label><input data-testid="profit-to" type="date" className={inputCls} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></div>
        <button data-testid="profit-apply" onClick={load} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors">Aplicar</button>
        <button data-testid="profit-csv" onClick={dlCsv} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground/75 hover:text-foreground hover:border-primary/50 transition-colors"><Download className="w-4 h-4" /> CSV</button>
      </div>
      {loading && <p className="text-sm text-muted-foreground py-4">Carregando...</p>}
      {p && !loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
            {[['Unidades vendidas', p.units, ''], ['Receita', formatCurrency(p.totals.sale_price), ''],
              ['Gastos', formatCurrency(p.totals.expenses), 'text-orange-400'],
              ['Back-End líquido', formatCurrency(p.totals.backend_net), 'text-fuchsia-400'],
              ['Lucro efetivo', formatCurrency(p.totals.profit), p.totals.profit >= 0 ? 'text-emerald-400' : 'text-red-400'],
              ['Lucro médio/carro', formatCurrency(p.avg_profit), '']].map(([l, val, cls]) => (
              <div key={l} className="rounded-xl border border-border/70 bg-card p-4">
                <p className={`font-display font-black text-xl tabular-nums ${cls}`}>{val}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{l}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-border/70 bg-card p-5 overflow-x-auto">
            <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Produtos de Back-End</p>
            <table className="w-full text-sm" data-testid="profit-products">
              <thead><tr className="text-left text-muted-foreground text-xs border-b border-border/70"><th className="pb-2">PRODUTO</th><th className="pb-2 text-center">VENDIDOS</th><th className="pb-2 text-center">PENETRAÇÃO</th><th className="pb-2 text-right">CUSTO</th><th className="pb-2 text-right">MARKUP</th><th className="pb-2 text-right">LUCRO</th></tr></thead>
              <tbody>
                {Object.entries(p.by_product).map(([k, b]) => (
                  <tr key={k} className="border-b border-border/50">
                    <td className="py-2.5 font-medium">{b.label}</td>
                    <td className="py-2.5 text-center">{b.units}</td>
                    <td className="py-2.5 text-center text-muted-foreground">{b.penetration}%</td>
                    <td className="py-2.5 text-right tabular-nums">{formatCurrency(b.cost)}</td>
                    <td className="py-2.5 text-right tabular-nums text-fuchsia-400">{formatCurrency(b.markup)}</td>
                    <td className="py-2.5 text-right tabular-nums font-bold text-emerald-400">{formatCurrency(b.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-border/70 bg-card p-5 overflow-x-auto">
            <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Carros vendidos no período</p>
            <table className="w-full text-sm" data-testid="profit-rows">
              <thead><tr className="text-left text-muted-foreground text-xs border-b border-border/70"><th className="pb-2">DATA</th><th className="pb-2">VEÍCULO</th><th className="pb-2 hidden lg:table-cell">CLIENTE</th><th className="pb-2 text-right">VENDA</th><th className="pb-2 text-right">CUSTO</th><th className="pb-2 text-right">GASTOS</th><th className="pb-2 text-right">BACK-END</th><th className="pb-2 text-right">LUCRO</th></tr></thead>
              <tbody>
                {p.rows.map((r) => (
                  <tr key={r.vehicle_id} data-testid={`profit-row-${r.stock}`} className="border-b border-border/50">
                    <td className="py-2.5 whitespace-nowrap text-muted-foreground">{r.sale_date || '—'}</td>
                    <td className="py-2.5"><p className="font-medium">{r.vehicle}</p><p className="text-[10px] text-muted-foreground/70">#{r.stock}</p></td>
                    <td className="py-2.5 hidden lg:table-cell text-foreground/75">{r.customer || '—'}</td>
                    <td className="py-2.5 text-right tabular-nums">{formatCurrency(r.sale_price)}</td>
                    <td className="py-2.5 text-right tabular-nums">{formatCurrency(r.vehicle_cost)}</td>
                    <td className="py-2.5 text-right tabular-nums text-orange-400">{formatCurrency(r.expenses)}</td>
                    <td className="py-2.5 text-right tabular-nums text-fuchsia-400">{formatCurrency(r.backend_net)}</td>
                    <td className={`py-2.5 text-right tabular-nums font-bold ${r.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(r.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {p.rows.length === 0 && <p className="text-center py-8 text-muted-foreground/70 text-sm">Nenhum carro vendido neste período.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitReport;
